import React, { FC, ReactElement } from 'react';
import { APIReferenceMember, MemberType, ReferenceType } from '@modules/client';
import { Typography } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import useReferenceSummaryMemberTableStyles from './ReferenceSummaryMemberTable.styles';
import ReferenceSummaryMemberTr from './ReferenceSummaryMemberTr';

import ViewInheritedFromLink, {
  InstanceReferenceMemberName,
  ObjectReferenceMemberName,
} from './ViewInheritedFromLink';
import { getReferencePageUrl, getSummaryTableId } from '../../utils/apiReferencePageNavUtils';

interface ReferenceSummaryMemberTableProps {
  memberType: MemberType;
  referenceType: ReferenceType;
  referenceName: string;
  title?: string;
  isInheritedMembers?: boolean;
  ariaLabel?: string;
  members: APIReferenceMember[];
  inheritedMembers?: Record<string, APIReferenceMember[]>;
  isEventMemberType: boolean;
}

const Container = ({
  id,
  isInheritedMembers,
  children,
  ariaLabel,
}: {
  id?: string;
  isInheritedMembers?: boolean;
  children: ReactElement;
  ariaLabel?: string;
}) => {
  const {
    classes: { container },
  } = useReferenceSummaryMemberTableStyles();
  return isInheritedMembers ? (
    children
  ) : (
    <div id={id} className={container} aria-label={ariaLabel}>
      {children}
    </div>
  );
};

const ReferenceSummaryMemberTable: FC<
  React.PropsWithChildren<ReferenceSummaryMemberTableProps>
> = ({
  memberType,
  title,
  referenceName,
  isInheritedMembers,
  ariaLabel = '',
  referenceType,
  members,
  inheritedMembers = {},
  isEventMemberType = false,
}) => {
  if (!title && !ariaLabel)
    throw new Error(`Missing title or aria-label on reference member table`);
  const { translateHTML } = useTranslation();
  const {
    classes: { header, inheritedFromLink, memberTable, tr, instanceTr },
    cx,
  } = useReferenceSummaryMemberTableStyles();
  // spec: if member's farthest ancestor is Instance or Object, render a link to its reference
  // page, instead of rendering a table
  return (
    <Container
      id={!isInheritedMembers ? getSummaryTableId(memberType) : undefined}
      isInheritedMembers={isInheritedMembers}
      aria-label={title ?? ariaLabel}>
      <React.Fragment>
        {!isInheritedMembers && title && (
          <Typography marginTop={1} className={cx(header)} variant='h5' component='h3'>
            {title}
          </Typography>
        )}
        {members.length > 0 && (
          <ul className={memberTable}>
            {members.map((member, i: number) => (
              <ReferenceSummaryMemberTr
                // eslint-disable-next-line react/no-array-index-key
                key={i.toString()}
                member={member}
                referenceType={referenceType}
                referenceName={referenceName}
                isInheritedMember={isInheritedMembers}
                isSameNameAsPrevMember={member.name === members[i - 1]?.name}
                ariaLabel={title ?? ariaLabel}
                isEventMemberType={isEventMemberType}
              />
            ))}
          </ul>
        )}
        {Object.keys(inheritedMembers)
          .filter((parentName) => inheritedMembers[parentName].length > 0)
          .map((parentName) => {
            if (parentName === InstanceReferenceMemberName) {
              return (
                <ViewInheritedFromLink
                  key='Instance'
                  className={cx(tr, instanceTr)}
                  memberType={memberType}
                  engineReferenceClassName={InstanceReferenceMemberName}
                />
              );
            }
            if (parentName === ObjectReferenceMemberName) {
              return (
                <ViewInheritedFromLink
                  key='Object'
                  className={cx(tr, instanceTr)}
                  memberType={memberType}
                  engineReferenceClassName={ObjectReferenceMemberName}
                />
              );
            }
            return (
              <React.Fragment key={parentName}>
                <Typography marginTop={6} marginBottom={0} className={header} variant='h6'>
                  {translateHTML('Label.MembersInheritedFromLinkedName', [
                    {
                      opening: 'membersNameStart',
                      closing: 'membersNameEnd',
                      content: () => title,
                    },
                    {
                      opening: 'parentLinkStart',
                      closing: 'parentLinkEnd',
                      // eslint-disable-next-line react/no-unstable-nested-components
                      content: () => (
                        <DynamicallyRouted.Link
                          href={getReferencePageUrl(referenceType, parentName)}
                          className={inheritedFromLink}>
                          {parentName}
                        </DynamicallyRouted.Link>
                      ),
                    },
                  ])}
                </Typography>
                <ReferenceSummaryMemberTable
                  memberType={memberType}
                  ariaLabel={title}
                  title={title}
                  referenceType={referenceType}
                  members={inheritedMembers[parentName]}
                  referenceName={parentName}
                  isEventMemberType={isEventMemberType}
                  isInheritedMembers={isInheritedMembers}
                />
              </React.Fragment>
            );
          })}
      </React.Fragment>
    </Container>
  );
};

export default ReferenceSummaryMemberTable;
